import { Add as AddIcon, Face as FaceIcon } from "@mui/icons-material";
import {
	Avatar,
	Button,
	Grid,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Paper,
	Stack,
	styled,
	Tooltip,
	Typography,
} from "@mui/material";
import { api } from "api";
import { Activity, Assignment, UserListBasic } from "api/types";
import crown from "assets/icons/crown.svg";
import { AlertDialog } from "components/AlertDialog";
import { SimpleActivityCard } from "components/SimpleActivityCard";
import { useAuthStatus } from "helpers/useAuthStatus";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "AssignmentDetailSummaryTab";

const classes = {
	dateText: `${PREFIX}-dateText`,
	editBtn: `${PREFIX}-editBtn`,
	activitiesHeader: `${PREFIX}-activitiesHeader`,
	dialog: `${PREFIX}-dialog`,
	rankingList: `${PREFIX}-rankingList`,
	grid: `${PREFIX}-grid`,
	gridItem: `${PREFIX}-gridItem`,
	crownIcon: `${PREFIX}-crownIcon`,
};

const Root = styled(Paper)(({ theme }) => ({
	[`& .${classes.dateText}`]: {
		marginBottom: theme.spacing(8),
		display: "inherit",
	},

	[`& .${classes.editBtn}`]: {
		display: "flex",
		justifyContent: "flex-end",
		paddingRight: "18.75rem",
		marginBottom: theme.spacing(2),
	},

	[`& .${classes.activitiesHeader}`]: {
		marginBottom: theme.spacing(1.2),
	},
	[`& .${classes.dialog}`]: {
		padding: theme.spacing(2),
	},

	[`& .${classes.crownIcon}`]: {
		width: "3rem",
		height: "3rem",
	},

	[`& .${classes.grid}`]: {
		overflowY: "auto",
		paddingRight: theme.spacing(1),
	},

	[`& .${classes.gridItem}`]: {
		[theme.breakpoints.down("lg")]: {
			justifyContent: "center",
		},
	},

	[`& .${classes.rankingList}`]: {
		overflow: "auto",
	},
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
	height: "60vh",
	padding: theme.spacing(2),
	margin: theme.spacing(2),
}));

export interface AssignmentDetailSummaryTabProps {
	assignment: Assignment;
	ranking: UserListBasic;
}

export const AssignmentDetailSummaryTab = ({
	assignment,
	ranking,
}: AssignmentDetailSummaryTabProps): JSX.Element => {
	const { role } = useAuthStatus();
	const canEdit =
		role === "admin" || role === "teacher" || role === "admin-teacher";
	const history = useHistory();
	const [activities, setActivities] = useState(assignment?.activities || []);
	const [deleteActivity, setDeleteActivity] = useState<Activity>();
	const [confirm, setConfirm] = useState(false);

	const handleDelete = async (): Promise<boolean> => {
		try {
			if (deleteActivity) {
				const res = await api.deleteActivitiesFromAssignment(
					assignment.assignmentId,
					[
						{
							questId: deleteActivity.questId,
							activityType: deleteActivity.activityType,
						},
					],
				);
				if (res.status === 204) {
					const index = activities.indexOf(deleteActivity);
					if (index !== -1) {
						setActivities(activities.filter((_, i) => i !== index));
					}
					setDeleteActivity(undefined);
					return true;
				}
			}
			throw new Error();
		} catch (error) {
			return false;
		}
	};

	const clickDelete = (item: Activity): void => {
		setConfirm(true);
		setDeleteActivity(item);
	};

	const onAddMore = (): void => {
		history.push(
			routes._.assignments._.edit.path.replace(
				":assignmentId",
				assignment.assignmentId,
			),
		);
	};

	return (
		<Root>
			<Grid container alignItems="stretch">
				<Grid item xs={12} md={7} lg={9}>
					<StyledPaper>
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							className={classes.activitiesHeader}
						>
							<Typography variant="h5">Assigned Activities</Typography>

							{canEdit && (
								<Tooltip title="Add activity">
									<Button
										color="secondary"
										variant="contained"
										onClick={onAddMore}
										data-testid="add-asm-btn"
										startIcon={<AddIcon />}
									>
										Add activity
									</Button>
								</Tooltip>
							)}
						</Stack>

						<Grid
							item
							gap={1}
							display="grid"
							gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))"
							alignItems="center"
							maxHeight="50vh"
							className={classes.grid}
						>
							{activities.map((item) => (
								<Grid
									key={`${item.questId} ${item.activityType} ${item.name}`}
									container
									height="100%"
									className={classes.gridItem}
								>
									<SimpleActivityCard
										show={canEdit && activities.length > 1}
										activity={item}
										remove={() => clickDelete(item)}
									/>
								</Grid>
							))}
						</Grid>
						<AlertDialog
							open={confirm}
							status={{
								success: "Successfully deleted activity!",
								failure: "Failed to delete activity.",
							}}
							onClose={() => setConfirm(false)}
							confirmAction={() => handleDelete()}
							title="Delete activity"
							message="Are you sure you want to delete this activity from the assignment?"
						/>
					</StyledPaper>
				</Grid>

				<Grid item xs={12} md={5} lg={3}>
					<StyledPaper>
						<Stack direction="row" alignItems="center">
							<Typography variant="h5">Leaderboard</Typography>
							<img src={crown} alt="crown" className={classes.crownIcon} />
						</Stack>

						<List className={classes.rankingList}>
							{ranking &&
								ranking.map((item) => {
									return (
										<ListItem key={item.userId}>
											<ListItemAvatar>
												<Avatar>
													<FaceIcon />
												</Avatar>
											</ListItemAvatar>
											<ListItemText
												primary={`${item.firstName} ${item.lastName}`}
											/>
										</ListItem>
									);
								})}
						</List>
					</StyledPaper>
				</Grid>
			</Grid>
		</Root>
	);
};
